import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import TableCard, { TableHeaderRow, tableCellClass } from '../../components/TableCard';
import CustomButton from '../../components/CustomButton';
import RowActionsMenu from '../../components/RowActionsMenu';
import { useGetMasterTenantsQuery } from '../../api/account/accountApiSlice';
import { MasterTenantContract, MasterTenantRow } from '../../api/account/accountApiTypes';
import { DEPRECATED_TENANT_SLUGS, LEGACY_TENANT_SLUGS } from '../../api/types';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import { useIsMaster } from '../../common/useIsMaster';
import { useAccessibleTenants } from '../../common/useAccessibleTenants';
import { useSwitchTenant } from '../../common/useSwitchTenant';
import { errorMessageFrom } from '../../utils/errorMessage';
import { formatDate } from '../../utils';
import { paths } from '../../routes';
import TenantContractDialog from './TenantContractDialog';

/**
 * The legacy tenants (own databases, no operational `tenant` row): listed
 * read-only, marked "Legacy", without the contract action (decision 6). The
 * retired v1 spellings are the same tenants as their `_v2` counterparts.
 */
export const legacyTenantSlugs = (): string[] =>
    Array.from(LEGACY_TENANT_SLUGS).filter((slug) => !DEPRECATED_TENANT_SLUGS.has(slug));

/**
 * The contract's tier as the list names it. "Individuell" carries its own
 * limit (PO, 2026-11-02): "Individuell · bis n Räume/Stationen" or
 * "Individuell · unbegrenzt"; a standard tier is its name.
 */
export const contractTierLabel = (contract: MasterTenantContract, t: TFunction): string => {
    if (contract.tier !== 'individual') return t(`master.tenants.tier.${contract.tier}`);
    return typeof contract.objectLimit === 'number'
        ? t('master.tenants.individual_limited', { count: contract.objectLimit })
        : t('master.tenants.individual_unlimited');
};

const badgeClass = 'inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-foreground/5 text-foreground';

/**
 * Master page "Mandanten" (plan tenant-management-first-step, ADR-0001
 * Amendment 6): every operational tenant with its type, solution, owner,
 * trial or contract, creation date and trial end; the legacy tenants below.
 * The row action switches a trial to a contract or changes the contract;
 * "Öffnen" switches into the tenant exactly as the tenant dropdown does
 * (`useSwitchTenant`, PO 2026-11-02) — archived tenants included, as the
 * dropdown lists them for reactivation, and legacy tenants only where the
 * user's `tenant:<slug>` grant lets the dropdown offer them.
 *
 * One request (`GET /admin/master/tenants`), not a per-tenant fan-out like the
 * other master pages, so it has its own chrome rather than
 * `MasterListScaffold`. Loading, a failure and an empty list each say which
 * they are.
 */
const MasterTenantsPage = () => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'de';
    const isMaster = useIsMaster();
    const { data, isLoading, isError, error, refetch, isFetching } = useGetMasterTenantsQuery(undefined, { skip: !isMaster });
    const [editing, setEditing] = useState<MasterTenantRow | null>(null);
    const legacy = useMemo(legacyTenantSlugs, []);
    const switchTenant = useSwitchTenant();
    // Legacy tenants are reachable by `tenant:<slug>` grant only — the same
    // grants (and aliases) the all-tenant view is built from.
    const accessibleTenants = useAccessibleTenants();
    const openableLegacy = useMemo(() => new Set(accessibleTenants), [accessibleTenants]);

    if (!isMaster) {
        return <Navigate to={paths.unauthorized} replace />;
    }

    const notApplicable = t('common.not_applicable');
    const day = (value: string | null) => (value ? formatDate(value, language) : notApplicable);
    const tenants = data ?? [];

    // Table row actions (PO, 2026-11-03; design-decisions.md › Zeilenaktionen
    // in Tabellen): the one action that only opens something is a text link in
    // the row; everything that changes something sits in the row's menu. The
    // page has no detail page, so the row itself is not clickable.
    const openButton = (slug: string, name: string) => (
        <button
            type={'button'}
            className={'text-primary hover:underline whitespace-nowrap'}
            aria-label={t('master.tenants.action_open_label', { name })}
            onClick={() => switchTenant(slug)}
        >
            {t('master.tenants.action_open')}
        </button>
    );

    const rowMenu = (row: MasterTenantRow) => (
        <RowActionsMenu
            rowName={row.organizationName}
            items={[{
                id: 'contract',
                label: t(row.isTrial ? 'master.tenants.action_switch' : 'master.tenants.action_change'),
                onClick: () => setEditing(row),
            }]}
        />
    );

    const contractCell = (row: MasterTenantRow) => {
        if (row.isTrial) return <span className={badgeClass}>{t('master.tenants.trial')}</span>;
        if (!row.contract) return <span className={'text-foreground/60'}>{t('common.missing')}</span>;
        return (
            <div>
                <div className={'text-foreground'}>{contractTierLabel(row.contract, t)}</div>
                <div className={'text-xs text-foreground/60'}>
                    {t('master.tenants.offer', { offerNumber: row.contract.offerNumber })}
                </div>
            </div>
        );
    };

    const columns = [
        t('master.tenants.columns.organization'),
        t('master.tenants.columns.type'),
        t('master.tenants.columns.solution'),
        t('master.tenants.columns.contact'),
        t('master.tenants.columns.contract'),
        t('master.tenants.columns.created'),
        t('master.tenants.columns.trial_end'),
        t('master.tenants.columns.actions'),
    ];

    const renderBody = () => {
        if (isLoading) {
            return <p className={'text-sm text-foreground/60'}>{t('master.tenants.loading')}</p>;
        }
        if (isError) {
            const unreachable = typeof (error as { status?: unknown } | undefined)?.status !== 'number';
            return (
                <div className={'flex flex-col items-start gap-3'} role={'alert'}>
                    <p className={'text-sm text-foreground'}>
                        {unreachable
                            ? t('master.tenants.load_failed_unreachable')
                            : errorMessageFrom(error, t('master.tenants.load_failed'))}
                    </p>
                    <CustomButton title={t('master.reload')} onClick={() => { void refetch(); }} isFilled={false} loading={isFetching} />
                </div>
            );
        }
        return (
            <TableCard>
                <TableHeaderRow labels={columns} />
                <tbody>
                    {tenants.length === 0 && (
                        <tr className={'border-b border-foreground/5'}>
                            <td className={tableCellClass} colSpan={columns.length}>{t('master.tenants.empty')}</td>
                        </tr>
                    )}
                    {tenants.map((row) => {
                        const archived = row.status?.toLowerCase() === 'archived';
                        return (
                            <tr key={row.id} className={'border-b border-foreground/5'}>
                                <td className={tableCellClass}>
                                    <div className={'text-foreground font-medium'}>{row.organizationName}</div>
                                    <div className={'text-xs text-foreground/60'}>{row.slug}</div>
                                    {archived && <span className={`${badgeClass} mt-1`}>{t('master.tenants.archived')}</span>}
                                </td>
                                <td className={tableCellClass}>{t(`organization_type.${row.organizationType}`)}</td>
                                <td className={tableCellClass}>{`kolula ${row.solution.toUpperCase()}`}</td>
                                <td className={tableCellClass}>
                                    {row.owner ? (
                                        <div>
                                            <div>{row.owner.name ?? notApplicable}</div>
                                            <div className={'text-xs text-foreground/60'}>{row.owner.email ?? notApplicable}</div>
                                        </div>
                                    ) : t('common.missing')}
                                </td>
                                <td className={tableCellClass}>{contractCell(row)}</td>
                                <td className={tableCellClass}>{day(row.createdAt)}</td>
                                <td className={tableCellClass}>{row.isTrial ? day(row.trialExpiresAt) : notApplicable}</td>
                                <td className={tableCellClass}>
                                    <div className={'flex items-center justify-between gap-3'}>
                                        {/* A slug that is also a legacy tenant is the legacy one
                                            in the dropdown, so it opens only under the grant. */}
                                        {(!LEGACY_TENANT_SLUGS.has(row.slug) || openableLegacy.has(row.slug)) && openButton(row.slug, row.organizationName)}
                                        {/* An archived tenant cannot take a contract: nothing for the menu. */}
                                        {!archived && rowMenu(row)}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </TableCard>
        );
    };

    return (
        <div className={'px-0 sm:px-4 py-6'}>
            <PageHeader>
                <div className={'grow'}>
                    <PageHeadline>{t('master.tenants.title')}</PageHeadline>
                    {data && (
                        <p className={'text-sm text-foreground/60'}>{t('master.tenants.count', { count: tenants.length })}</p>
                    )}
                </div>
            </PageHeader>

            {renderBody()}

            <h2 className={'mt-8 mb-3 text-base font-semibold text-foreground'}>{t('master.tenants.legacy_heading')}</h2>
            <TableCard>
                <TableHeaderRow
                    labels={[
                        t('master.tenants.columns.organization'),
                        t('master.tenants.columns.contract'),
                        t('master.tenants.columns.actions'),
                    ]}
                />
                <tbody>
                    {legacy.map((slug) => (
                        <tr key={slug} className={'border-b border-foreground/5'}>
                            <td className={tableCellClass}>
                                <div className={'text-foreground font-medium'}>{getTenantDisplayName(slug)}</div>
                                <div className={'text-xs text-foreground/60'}>{slug}</div>
                            </td>
                            <td className={tableCellClass}>
                                <span className={badgeClass}>{t('master.tenants.legacy')}</span>
                            </td>
                            <td className={tableCellClass}>{openableLegacy.has(slug) && openButton(slug, getTenantDisplayName(slug))}</td>
                        </tr>
                    ))}
                </tbody>
            </TableCard>

            {editing && <TenantContractDialog tenant={editing} onClose={() => setEditing(null)} />}
        </div>
    );
};

export default MasterTenantsPage;
